/**
 * Extraction Result Modal - Display and confirm AI-extracted declaration data
 */
import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../../../store/authStore';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Grid,
  Alert,
  LinearProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  CheckCircle as ConfirmIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import ComplianceCheckPanel from './ComplianceCheckPanel';

interface ExtractionResult {
  declaration_type?: string;
  product_description?: string;
  hs_code?: string;
  origin_country?: string;
  valid_from?: string;
  valid_until?: string;
  certificate_number?: string;
  confidence_score: number;
  extracted_text: string;
  processing_status: string;
}

interface ExtractionResultModalProps {
  open: boolean;
  declarationId: string;
  extractionResult: ExtractionResult | null;
  onClose: () => void;
  onConfirm: () => void;
}

const ExtractionResultModal: React.FC<ExtractionResultModalProps> = ({
  open,
  declarationId,
  extractionResult,
  onClose,
  onConfirm,
}) => {
  const token = useAuthStore((state) => state.token);
  const [formData, setFormData] = useState({
    declaration_type: '',
    product_description: '',
    hs_code: '',
    origin_country: '',
    valid_from: '',
    valid_until: '',
    certificate_number: '',
  });
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (extractionResult) {
      setFormData({
        declaration_type: extractionResult.declaration_type || '',
        product_description: extractionResult.product_description || '',
        hs_code: extractionResult.hs_code || '',
        origin_country: extractionResult.origin_country || '',
        valid_from: extractionResult.valid_from || '',
        valid_until: extractionResult.valid_until || '',
        certificate_number: extractionResult.certificate_number || '',
      });
    }
  }, [extractionResult]);

  const getConfidenceColor = (score: number) => {
    if (score >= 0.8) return 'success';
    if (score >= 0.6) return 'warning';
    return 'error';
  };

  const getConfidenceLabel = (score: number) => {
    if (score >= 0.8) return 'High Confidence';
    if (score >= 0.6) return 'Medium Confidence';
    return 'Low Confidence';
  };

  const handleConfirm = async () => {
    setConfirming(true);
    setError(null);

    try {
      const response = await fetch(
        `/api/v1/supplier-declarations/declarations/${declarationId}/confirm`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token?.access_token}`,
          },
          body: JSON.stringify(formData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to confirm data');
      }

      // Call success callback
      onConfirm();
      
      // Reset state
      setFormData({
        declaration_type: '',
        product_description: '',
        hs_code: '',
        origin_country: '',
        valid_from: '',
        valid_until: '',
        certificate_number: '',
      });
    } catch (err: any) {
      setError(err.message || 'Failed to confirm declaration data');
    } finally {
      setConfirming(false);
    }
  };

  const handleClose = () => {
    if (!confirming) {
      setError(null);
      onClose();
    }
  };

  if (!extractionResult) return null;

  const confidenceScore = extractionResult.confidence_score;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Confirm Extracted Data</Typography>
          <Chip
            icon={
              confidenceScore >= 0.6 ? (
                <ConfirmIcon />
              ) : (
                <WarningIcon />
              )
            }
            label={`${getConfidenceLabel(confidenceScore)} (${(confidenceScore * 100).toFixed(0)}%)`}
            color={getConfidenceColor(confidenceScore)}
            size="small"
          />
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {confidenceScore < 0.6 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Low confidence score detected. Please review and edit the extracted data carefully.
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
            Review and edit the extracted information below, then click Confirm to save.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Declaration Type</InputLabel>
                <Select
                  value={formData.declaration_type}
                  onChange={(e) =>
                    setFormData({ ...formData, declaration_type: e.target.value })
                  }
                  label="Declaration Type"
                >
                  <MenuItem value="origin">Origin</MenuItem>
                  <MenuItem value="preferential">Preferential</MenuItem>
                  <MenuItem value="quality">Quality</MenuItem>
                  <MenuItem value="safety">Safety</MenuItem>
                  <MenuItem value="environmental">Environmental</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="HS Code"
                value={formData.hs_code}
                onChange={(e) => setFormData({ ...formData, hs_code: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Product Description"
                multiline
                rows={3}
                value={formData.product_description}
                onChange={(e) =>
                  setFormData({ ...formData, product_description: e.target.value })
                }
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Origin Country"
                value={formData.origin_country}
                onChange={(e) =>
                  setFormData({ ...formData, origin_country: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Certificate Number"
                value={formData.certificate_number}
                onChange={(e) =>
                  setFormData({ ...formData, certificate_number: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Valid From"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.valid_from}
                onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Valid Until"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.valid_until}
                onChange={(e) =>
                  setFormData({ ...formData, valid_until: e.target.value })
                }
              />
            </Grid>
          </Grid>

          {/* Compliance Check */}
          <ComplianceCheckPanel
            hsCode={formData.hs_code}
            originCountry={formData.origin_country}
            productDescription={formData.product_description}
          />

          {confirming && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Saving changes...
              </Typography>
              <LinearProgress />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={confirming}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!formData.product_description || confirming}
          startIcon={<ConfirmIcon />}
        >
          {confirming ? 'Confirming...' : 'Confirm'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExtractionResultModal;
